import { right, left } from "fp-ts/lib/Either";
import Constants, { ConstantsName } from "../../../../../constants/Constants";
import di from "../../../../../di/DependencyInjection";
import ExceptionEntity from "../../../../../domain/entities/ExceptionEntity";
import { Auth } from "aws-amplify";

interface UploadChannelParams {
    customer: string;
    lob: string;
    user_id: string;
    date: string;
    channel: string;
    file?: File;
}

const parseErrorMessage = async (response: Response): Promise<string> => {
    const contentType = response.headers.get('content-type') || '';
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

    try {
        if (contentType.includes('application/json')) {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
        } else if (contentType.includes('text/html')) {
            const htmlText = await response.text();
            const titleMatch = htmlText.match(/<title>(.*?)<\/title>/i);
            const h1Match = htmlText.match(/<h1>(.*?)<\/h1>/i);
            const pMatch = htmlText.match(/<p>(.*?)<\/p>/i);
            errorMessage = titleMatch?.[1] || h1Match?.[1] || pMatch?.[1]
                || `Server error (${response.status}): The server encountered an internal error`;
        } else {
            const text = await response.text();
            if (text) errorMessage = text;
        }
    } catch {
        // fallback al mensaje por defecto
    }

    return errorMessage;
};

/**
 * Calls the /upload-channel endpoint.
 * - With file: GET presigned URL and uploads to S3 (two-step).
 * - Without file: GET with query params (backward compatibility).
 */
const UploadChannelApiImpl = async (params: UploadChannelParams): Promise<any> => {
    try {
        const baseUrl = di.get<Constants>(ConstantsName).AI;
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken() || '';

        const queryParams = new URLSearchParams({
            customer: params.customer,
            lob: params.lob,
            user_id: params.user_id,
            date: params.date,
            channel: params.channel,
            ...(params.file && { filename: params.file.name }), // ← solo si hay file
        });

        const url = `${baseUrl}/upload-channel?${queryParams.toString()}`;

        if (params.file) {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Authorization': token },
            });

            if (!response.ok) {
                const message = await parseErrorMessage(response);
                return left({ message, code: `ErrorHost${response.status}` } as ExceptionEntity);
            }

            const { upload_url } = await response.json();

            const s3Response = await fetch(upload_url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/octet-stream' },
                body: params.file,
            });

            if (!s3Response.ok) {
                return left({
                    message: `S3 upload failed: ${s3Response.status}`,
                    code: `ErrorHostS3${s3Response.status}`
                } as ExceptionEntity);
            }

            return right({ message: 'File uploaded successfully' });
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token,
            },
        });

        if (!response.ok) {
            const message = await parseErrorMessage(response);
            return left({ message, code: `ErrorHost${response.status}` } as ExceptionEntity);
        }

        try {
            const data = await response.json();
            return right(data);
        } catch {
            const text = await response.text();
            return right(text);
        }

    } catch (error: any) {
        return left({
            message: error.message || 'Failed to upload channel data',
            code: 'ErrorHostUploadChannel'
        } as ExceptionEntity);
    }
};

export default UploadChannelApiImpl;